import React, { useMemo } from 'react';
import scaleBand from '@visx/scale/lib/scales/band';
import { Group } from '@visx/group';
import { interpolateRgb } from 'd3-interpolate';
import { useThemeColors } from '../../hooks/useThemeColors';
import ChartA11y from './ChartA11y';

const isDev = process.env.NODE_ENV !== 'production';
const SEVERITY_ROWS = ['critical', 'high', 'medium', 'low'];
const SEVERITY_LABELS = { critical: 'Critical', high: 'High', medium: 'Medium', low: 'Low' };

const DEFAULT_MARGIN = { top: 24, right: 16, bottom: 28, left: 70 };

export default function SeverityHeatcalendar({
  data,
  width = 800,
  height = 200,
  title,
  desc,
  className,
  style,
  periodLabel = 'period',
  margin = DEFAULT_MARGIN,
}) {
  const colors = useThemeColors();

  if (isDev && (!title || typeof title !== 'string')) {
    throw new Error(
      'SeverityHeatcalendar: `title` prop is required for accessibility (see PRD-69 §3.2.0).'
    );
  }

  const rows = useMemo(() => {
    if (!Array.isArray(data)) return [];
    return data.map((d, i) => ({
      key: d.date || d.label || String(i),
      label: d.label || d.date || `${periodLabel} ${i + 1}`,
      critical: Number.isFinite(d.critical) ? d.critical : 0,
      high: Number.isFinite(d.high) ? d.high : 0,
      medium: Number.isFinite(d.medium) ? d.medium : 0,
      low: Number.isFinite(d.low) ? d.low : 0,
    }));
  }, [data, periodLabel]);

  const innerW = Math.max(0, width - margin.left - margin.right);
  const innerH = Math.max(0, height - margin.top - margin.bottom);

  const xScale = useMemo(
    () =>
      scaleBand({
        domain: rows.map((r) => r.key),
        range: [0, innerW],
        padding: 0.08,
      }),
    [rows, innerW]
  );

  const yScale = useMemo(
    () =>
      scaleBand({
        domain: SEVERITY_ROWS,
        range: [0, innerH],
        padding: 0.18,
      }),
    [innerH]
  );

  // Per-row max (one severity may dwarf others). Per-row scaling keeps low-count rows visible.
  const rowMax = useMemo(() => {
    const max = { critical: 0, high: 0, medium: 0, low: 0 };
    for (const r of rows) {
      for (const sev of SEVERITY_ROWS) {
        if (r[sev] > max[sev]) max[sev] = r[sev];
      }
    }
    return max;
  }, [rows]);

  // Theme-aware color interpolator per severity — bans d3-scale-chromatic per PRD-69 §3.4.
  const interpolators = useMemo(
    () => ({
      critical: interpolateRgb(colors.bg.card, colors.severity.critical),
      high: interpolateRgb(colors.bg.card, colors.severity.high),
      medium: interpolateRgb(colors.bg.card, colors.severity.medium),
      low: interpolateRgb(colors.bg.card, colors.severity.low),
    }),
    [colors]
  );

  if (rows.length === 0) {
    return (
      <ChartA11y title={title} desc={desc || 'No severity data to render.'} width={width} height={height} className={className} style={style}>
        <text
          x={width / 2}
          y={height / 2}
          textAnchor="middle"
          fontSize={12}
          fill={colors.text.muted}
          fontFamily="inherit"
        >
          No severity data
        </text>
      </ChartA11y>
    );
  }

  // x-tick stride: cap visible tick labels at ~10 to avoid overlap on narrow widths
  const tickStride = Math.max(1, Math.ceil(rows.length / 10));

  return (
    <ChartA11y title={title} desc={desc} width={width} height={height} className={className} style={style}>
      <Group left={margin.left} top={margin.top}>
        {/* Severity row labels */}
        {SEVERITY_ROWS.map((sev) => {
          const y = yScale(sev) + yScale.bandwidth() / 2;
          return (
            <text
              key={`row-label-${sev}`}
              x={-8}
              y={y}
              dy="0.32em"
              textAnchor="end"
              fontSize={12}
              fill={colors.text.secondary}
              fontFamily="inherit"
            >
              {SEVERITY_LABELS[sev]}
            </text>
          );
        })}

        {/* Cells */}
        {rows.map((r) =>
          SEVERITY_ROWS.map((sev) => {
            const count = r[sev];
            const max = rowMax[sev] || 1;
            const t = count === 0 ? 0 : Math.min(1, count / max);
            const fill = t === 0 ? colors.bg.card : interpolators[sev](Math.max(0.18, t));
            const x = xScale(r.key) ?? 0;
            const y = yScale(sev) ?? 0;
            const w = xScale.bandwidth();
            const h = yScale.bandwidth();
            return (
              <rect
                key={`${r.key}-${sev}`}
                x={x}
                y={y}
                width={w}
                height={h}
                rx={2}
                ry={2}
                fill={fill}
                stroke={colors.border.medium}
                strokeWidth={0.5}
              >
                <title>{`${r.label} — ${SEVERITY_LABELS[sev]}: ${count}`}</title>
              </rect>
            );
          })
        )}

        {/* X-axis tick labels (stride-sampled) */}
        {rows.map((r, i) => {
          if (i % tickStride !== 0) return null;
          const x = (xScale(r.key) ?? 0) + xScale.bandwidth() / 2;
          return (
            <text
              key={`x-tick-${r.key}`}
              x={x}
              y={innerH + 16}
              textAnchor="middle"
              fontSize={10}
              fill={colors.text.muted}
              fontFamily="inherit"
            >
              {r.label}
            </text>
          );
        })}
      </Group>
    </ChartA11y>
  );
}
